import React, { useContext } from 'react'

import { ArticleHero } from '../../../publication/ArticleHero/ArticleHero'
import { ArticleBlockContent } from '../../../publication/ArticleBlockContent/ArticleBlockContent'
import { Headline } from '../../../publication/Headline/Headline'
import { Byline } from '../../../publication/Byline/Byline'
import { Footnote } from '../../../publication/Footnote/Footnote'

import { TopicList } from '../../../content/TopicList/TopicList'
import {
    StyledArticleContent,
    StyledGrid,
    StyledCell,
    StyledNestedGrid,
    StyledSharing,
    StyledAside,
    StyledStickyTop,
    StyledStickyCell,
    StyledVideoCell,
} from '../../../templates/Publication/SevenNewsPublication/SevenNewsPublication.styled'

import { getCanonicalLink } from '../../../templates/Publication/lib/get-canonical-link'
import { PublicationProps } from '../../../templates/Publication/Publication.props'
import { getFundingType } from '../../../templates/Publication/lib/get-funding-type'

import { SponsoredTaboola } from '../../../advertising/Taboola/Taboola'
import { RenderTargetContext } from '@news-mono/web-common'
import { useFeature } from '@news-mono/web-common'
import { getUpdatedTime } from '../../../__helpers/get-updated-time'
import { getEditorialType } from '../lib/get-editorial-type'
import { EditorialLabel } from '../../../typography/EditorialLabel/EditorialLabel'

export const videoContainerClassName = 'sticky-container'

export const SevenNewsPublication: React.SFC<PublicationProps> = (props) => {
    const { renderTarget } = useContext(RenderTargetContext)
    const publication = props.data
    const content = publication.content
    const canonicalLink = getCanonicalLink(publication)
    const heroImage = publication.heroImage
    const targetedContentElement =
        props.targetedContent && props.targetedContent.position === 'inline'
            ? props.targetedContent.element
            : undefined

    const storyClassification = useFeature('story-classification')
    const fundingType = getFundingType(publication, storyClassification)
    const editorialType = getEditorialType(publication, storyClassification)

    const heroVideo =
        props.heroOptions.isVideoStickyEnabled === true ? (
            <React.Fragment>
                <StyledStickyTop className="sticky-top" />
                <StyledVideoCell className={videoContainerClassName}>
                    <StyledStickyCell className="sticky-cell" largeGap={true}>
                        <ArticleHero
                            heroVideo={publication.heroVideo}
                            heroImage={heroImage}
                            preferredCaptionSource={'video'}
                            heading={publication.heading}
                            onEvent={props.onEvent}
                            showVideoTitles={true}
                            autoplayNextOptionssOverrides={{ delayDuration: 5 }}
                            isVideoStickyEnabled={true}
                            getVideoQueue={props.getVideoQueue}
                            publicationPathname={props.location.pathname}
                            adUnitPath={props.adUnitPath}
                        />
                    </StyledStickyCell>
                </StyledVideoCell>
            </React.Fragment>
        ) : (
            <StyledCell largeGap={true}>
                <ArticleHero
                    heroVideo={publication.heroVideo}
                    heroImage={heroImage}
                    preferredCaptionSource={'video'}
                    heading={publication.heading}
                    onEvent={props.onEvent}
                    showVideoTitles={true}
                    autoplayNextOptionssOverrides={{ delayDuration: 5 }}
                    isVideoStickyEnabled={false}
                    getVideoQueue={props.getVideoQueue}
                    publicationPathname={props.location.pathname}
                    adUnitPath={props.adUnitPath}
                />
            </StyledCell>
        )

    return (
        <StyledGrid>
            {editorialType && (
                <StyledCell>
                    <EditorialLabel type={editorialType} isLarge />
                </StyledCell>
            )}

            <StyledCell>
                <Headline text={publication.heading} />
            </StyledCell>

            <StyledCell>
                <Byline
                    profile={publication.profile}
                    text={publication.byline}
                    timestamp={publication.publicationDate}
                    timestampUpdated={getUpdatedTime(
                        publication.publicationDate,
                        publication.lastUpdated,
                    )}
                    fundingType={fundingType}
                    source={publication.source}
                    isFullWidth={true}
                    url={publication._self}
                    title={publication.heading}
                    requiredAccess={publication.requiredAccess}
                    allowCommenting={false}
                />
            </StyledCell>

            {heroVideo}

            <StyledCell largeGap={true}>
                <StyledNestedGrid>
                    <StyledAside>
                        <StyledSharing
                            url={publication._self}
                            text={publication.heading}
                            onEvent={props.onEvent}
                            shareOptions={['facebook', 'twitter', 'email']}
                        />
                    </StyledAside>
                    <div>
                        {content && (
                            <StyledArticleContent>
                                <ArticleBlockContent
                                    blocks={content}
                                    showInlineVideoTitles={true}
                                    targetedContentElement={
                                        targetedContentElement
                                    }
                                    inlinePositionedContent={
                                        props.inlinePositionedContent
                                    }
                                    onEvent={props.onEvent}
                                    log={props.log}
                                    publicationPathname={
                                        props.location.pathname
                                    }
                                    adUnitPath={props.adUnitPath}
                                    getVideoQueue={props.getVideoQueue}
                                />
                            </StyledArticleContent>
                        )}
                    </div>
                </StyledNestedGrid>
            </StyledCell>
            <StyledCell largeGap={true}>
                <TopicList
                    primaryTopic={publication.primaryTopic}
                    secondaryTopics={publication.secondaryTopics}
                />
            </StyledCell>
            {canonicalLink && (
                <StyledCell largeGap={true}>
                    <Footnote
                        text="Originally published as "
                        link={canonicalLink}
                    />
                </StyledCell>
            )}

            {/* Taboola needs to be manually split around Plista on AMP */}
            {renderTarget === 'amp' ? (
                <React.Fragment>
                    <SponsoredTaboola
                        taboolaContainerId="taboola-below-article-thumbnails"
                        locationType="article"
                        articleId={publication.id}
                        ampPosition={1}
                    />
                    <SponsoredTaboola
                        taboolaContainerId="taboola-below-article-thumbnails"
                        locationType="article"
                        articleId={publication.id}
                        ampPosition={2}
                    />
                    <SponsoredTaboola
                        taboolaContainerId="taboola-below-article-thumbnails"
                        locationType="article"
                        articleId={publication.id}
                        ampPosition={3}
                    />
                </React.Fragment>
            ) : (
                <SponsoredTaboola
                    taboolaContainerId="taboola-below-article-thumbnails"
                    locationType="article"
                    articleId={publication.id}
                />
            )}
        </StyledGrid>
    )
}
SevenNewsPublication.displayName = 'SevenNewsPublication'
